import * as React from "react";
import { motion } from "framer-motion";
import type { SellerAnalysis } from "@/data/sellerAnalysisTypes";
import { isPdfRenderMode } from "@/lib/pdfMode";

interface Props {
  data: SellerAnalysis;
}

const EASE = [0.22, 1, 0.36, 1] as const;

export const SA_Hero: React.FC<Props> = ({ data }) => {
  const pdfMode = isPdfRenderMode();
  const { owner, property, scrollCue, hero } = data;
  const cover = property.images?.[0];
  const overline =
    hero?.eyebrow ||
    (owner?.firstName ? `Prepared for ${owner.firstName}` : "A private analysis");
  const headline =
    hero?.headline ||
    `${property.address}${property.unit ? `, ${property.unit}` : ""}`;
  const body =
    hero?.body?.length
      ? hero.body
      : [
          "Before discussing a listing strategy, I wanted to understand what the market is actually telling us about your property.",
          "I reviewed the most relevant recent sales, your building, the property's positioning and the economics of a potential sale.",
        ];

  return (
    <section
      className="relative flex min-h-[92vh] w-full items-end overflow-hidden bg-charcoal"
      data-testid="sa-hero"
      aria-label="Introduction"
    >
      {cover && (
        <div className="absolute inset-0">
          <img
            src={cover.url}
            alt={cover.alt || property.address}
            className="hero-kenburns h-full w-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-charcoal/25" />
          <div className="absolute inset-0 grain-overlay opacity-[0.12]" />
        </div>
      )}

      <div className="relative z-10 mx-auto w-full max-w-5xl px-6 pb-16 pt-28 md:px-10 md:pb-24">
        <motion.div
          initial={pdfMode ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: pdfMode ? 0 : 0.8, ease: EASE, delay: pdfMode ? 0 : 0.15 }}
          className="max-w-3xl"
        >
          <div className="mb-6 flex items-center gap-3 text-ivory/70">
            <span className="h-px w-8 bg-brass" />
            <span className="text-[0.7rem] uppercase tracking-[0.28em]">{overline}</span>
          </div>

          {!hero?.headline && (
            <p className="font-serif text-2xl italic text-ivory/85 md:text-3xl">
              A closer look at
            </p>
          )}
          <h1
            className={`font-serif text-4xl font-medium leading-[1.05] text-ivory sm:text-5xl lg:text-6xl ${
              hero?.headline ? "" : "mt-1"
            }`}
          >
            {headline}
          </h1>

          <motion.div
            initial={pdfMode ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: pdfMode ? 0 : 0.8, ease: EASE, delay: pdfMode ? 0 : 0.4 }}
            className="mt-7 max-w-xl space-y-4 text-base leading-relaxed text-ivory/85 md:text-lg"
          >
            {body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </motion.div>

          {hero?.callout && (
            <motion.p
              initial={pdfMode ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: pdfMode ? 0 : 0.8, ease: EASE, delay: pdfMode ? 0 : 0.55 }}
              className="mt-6 max-w-xl border-l-2 border-brass pl-5 font-serif text-xl italic leading-snug text-ivory md:text-2xl"
            >
              {hero.callout}
            </motion.p>
          )}

          {scrollCue && !pdfMode && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="mt-10 flex items-center gap-3 text-ivory/70"
              data-pdf-hide
            >
              <span className="text-xs uppercase tracking-[0.24em]">{scrollCue}</span>
              <motion.span
                aria-hidden
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="text-brass"
              >
                ↓
              </motion.span>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
